/**
 * Catalog fetcher with hybrid Strykr-static + community-DB-direct
 * sourcing (Phase 8.7).
 *
 * Two sources merged for display:
 *
 *   1. **Static** `/v1/catalog.json` — Strykr's curated set, baked
 *      into the marketplace-web build at prebuild from
 *      `/catalog/catalog.json`. CDN-cached, ~10ms read.
 *
 *   2. **DB-direct** `marketplace-api/v1/catalog/community` —
 *      community uploads stored in Neon. Approve→live latency ~1s
 *      (DB write) + 60s CDN cache TTL. Each row's inline ed25519
 *      signature is re-verified server-side on every read.
 *
 * Failure handling:
 *   - Static failure → throws; caller renders error state.
 *   - Community failure → silently fall back to Strykr-only set;
 *     a transient API outage shouldn't break browse on the
 *     statically-bundled catalog.
 */
import type { CatalogIndex, Strategy } from "./types.js";
import { MARKETPLACE_API_URL } from "./marketplace-api.js";

/**
 * Resolve the catalog.json URL relative to the deployed base path.
 * Vite's BASE_URL is "/" for standalone deploys and "/strategies/"
 * for the Phase-13 unified-site deploy. We need the catalog to load
 * from the same prefix the rest of the assets live under.
 *
 * Untyped cast mirrors the workaround in marketplace-api.ts — the
 * marketplace-web tsconfig doesn't reference vite/client types.
 */
function catalogUrl(): string {
  const base = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL ?? "/";
  // Ensure exactly one slash between base and v1/.
  return base.replace(/\/+$/, "") + "/v1/catalog.json";
}

/** Fetches the static Strykr catalog from the same origin. Throws
 *  on network error / non-200. */
async function fetchStaticCatalog(): Promise<CatalogIndex> {
  const res = await fetch(catalogUrl(), {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) {
    throw new Error(`catalog fetch failed: ${res.status} ${res.statusText}`);
  }
  const json = (await res.json()) as CatalogIndex;
  if (!Array.isArray(json.strategies)) {
    throw new Error(`catalog response missing "strategies" array`);
  }
  return json;
}

interface CommunityResponse {
  ok: boolean;
  rows: Strategy[];
  skipped?: number;
  total?: number;
}

/** Fetches the DB-direct community catalog. Returns an empty array
 *  on any failure (network error, non-200, malformed body) so a
 *  transient API outage doesn't block browse. */
async function fetchCommunityCatalog(): Promise<Strategy[]> {
  try {
    const res = await fetch(`${MARKETPLACE_API_URL}/v1/catalog/community`, {
      headers: { Accept: "application/json" },
    });
    if (!res.ok) return [];
    const body = (await res.json()) as CommunityResponse;
    if (!body.ok || !Array.isArray(body.rows)) return [];
    return body.rows;
  } catch {
    return [];
  }
}

/** Fetches both sources in parallel and merges. Strykr's curated
 *  rows always render even if the community endpoint is unreachable.
 *  Community rows append after Strykr; the UI applies its own sort
 *  (by popularity, recency, etc.) on top. */
export async function fetchCatalog(): Promise<CatalogIndex> {
  const [staticIdx, communityRows] = await Promise.all([
    fetchStaticCatalog(),
    fetchCommunityCatalog(),
  ]);
  // Dedup: a strategy id present in both sources prefers the static
  // (Strykr-curated) row. Strykr-published manifests should never
  // collide with community uploads in practice (different `kind`,
  // different id slugs), but the guard keeps render deterministic.
  const seen = new Set(staticIdx.strategies.map((s) => s.id));
  const merged = [
    ...staticIdx.strategies,
    ...communityRows.filter((r) => !seen.has(r.id)),
  ];
  return { ...staticIdx, strategies: merged };
}
